import { useCallback, useState } from "react";

export type AddRequirementTab = "library" | "new";

interface Options<T> {
  /** The section's current list — what a batch is appended to, and what Undo restores. */
  items: T[];
  /** Writes the list back. The hook never decides how a section persists, only what it persists. */
  commit: (next: T[]) => void;
}

/**
 * The state every requirement section needs around the add-from-library panel: which tab it opens on,
 * what was last added, and the confirmation that offers to undo it.
 *
 * Each section grew its own copy of this while the panel was wired up section by section, including
 * the rule that any other change dismisses the confirmation — which is exactly the kind of rule that
 * gets forgotten in the seventh copy.
 */
export const useAddFromLibrary = <T,>({ items, commit }: Options<T>) => {
  const [panel, setPanel] = useState<{ tab: AddRequirementTab } | null>(null);
  const [lastAdded, setLastAdded] = useState<{ count: number; previous: T[] } | null>(null);

  const dismiss = useCallback(() => setLastAdded(null), []);
  const openPanel = useCallback((tab: AddRequirementTab) => setPanel({ tab }), []);
  const closePanel = useCallback(() => setPanel(null), []);

  return {
    isPanelOpen: panel !== null,
    tab: panel?.tab ?? "library",
    openPanel,
    closePanel,

    lastAdded,
    /** The trailing rows to mark as just added — none once the confirmation is gone. */
    markedCount: lastAdded?.count ?? 0,

    /**
     * Any change to the list dismisses the confirmation: it describes a state the section has moved
     * on from, and its Undo would restore the wrong thing. Sections route every other write here.
     */
    save: (next: T[]) => {
      dismiss();
      commit(next);
    },

    /**
     * The one write that raises the confirmation rather than clearing it.
     *
     * `base` is what the new rows are appended to, for a section that replaces as it adds rather than
     * only appending — the § 5b route drops the exemption marker in the same edit, since an exemption
     * and an environment criterion never stand together. Undo still restores `items`, marker and all:
     * it means "as it was", not "as it was halfway through".
     */
    add: (added: T[], base: T[] = items) => {
      if (added.length === 0) return;
      commit([...base, ...added]);
      setLastAdded({ count: added.length, previous: items });
    },

    undo: () => {
      if (!lastAdded) return;
      dismiss();
      commit(lastAdded.previous);
    },

    /** For sections that own their own write path (debounced saves) but still must dismiss. */
    dismiss,
  };
};
